<div class="container mx-auto px-4 py-6">
  <div class="flex items-start gap-6">
    <!-- Desktop navigation: in the flow, so it never slides under the navbar -->
    <aside class="hidden w-64 shrink-0 lg:block">
      <div class="sticky top-20">
        <app-settings-sidebar
          [activeParam]="activeParam"
          [isMember]="!!company?.is_member"
          [isNetwork]="!!company?.is_group_admin"
          (select)="onSelect($event)" />
      </div>
    </aside>

    <main class="min-w-0 max-w-3xl flex-1 space-y-6">
      <header class="flex flex-col gap-3">
        <!-- Mobile: the same navigation inside a sheet. The trigger is a labelled
             button, because a bare panel icon didn't read as "more settings". -->
        <hlm-sheet
          side="left"
          class="lg:hidden"
          [state]="navOpen ? 'open' : 'closed'"
          (stateChanged)="navOpen = $event === 'open'">
          <button
            hlmSheetTrigger
            hlmBtn
            variant="outline"
            class="w-full justify-between"
            aria-label="Alle Einstellungen anzeigen">
            <span class="flex min-w-0 items-center gap-2">
              <ng-icon name="lucideSettings2" />
              <span class="truncate">Alle Einstellungen</span>
            </span>
            <ng-icon name="lucideChevronRight" data-icon="inline-end" />
          </button>

          <hlm-sheet-content *hlmSheetPortal class="w-72 p-3" aria-label="Einstellungen-Navigation">
            <app-settings-sidebar
              [activeParam]="activeParam"
              [isMember]="!!company?.is_member"
              [isNetwork]="!!company?.is_group_admin"
              (select)="onSelect($event)"
              (picked)="navOpen = false" />
          </hlm-sheet-content>
        </hlm-sheet>

        <h1 hlmH3>{{ currentTitle }}</h1>
      </header>

      <!-- Panels stay mounted and are toggled via display so that form state,
           Stripe embeds and loaded data survive a tab switch. -->
      <div [style.display]="isActiveParam('sell-settings') ? 'block' : 'none'">
        <app-sell-settings />
      </div>

      <div [style.display]="isActiveParam('storefront') ? 'block' : 'none'">
        <app-storefront-settings />
      </div>

      <div [style.display]="isActiveParam('qr-code') ? 'block' : 'none'">
        <app-qr-code-settings />
      </div>

      <div [style.display]="isActiveParam('voucher-design') ? 'block' : 'none'">
        <app-voucher-design-settings />
      </div>

      <!-- Fees and PIN share one page but keep separate forms, so each card
           still saves on its own without touching the other's field. -->
      <div class="space-y-6" [style.display]="isActiveParam('fees-redemption') ? 'block' : 'none'">
        @if (!company?.is_member) {
          <app-fees-settings />
        }
        @if (!company?.is_group_admin) {
          <app-redemption-pin-settings />
        }
      </div>

      <!-- Rendered only while open: it loads the referral overview on creation. -->
      <div [style.display]="isActiveParam('recommend') ? 'block' : 'none'">
        @if (isActiveParam('recommend')) {
          <app-referral-settings />
        }
      </div>

      <div [style.display]="isActiveParam('integration') ? 'block' : 'none'">
        <app-integration-settings />
      </div>

      <div [style.display]="isActiveParam('verbund') ? 'block' : 'none'">
        <app-verbund-settings />
      </div>

      <div [style.display]="isActiveParam('verbund-members') ? 'block' : 'none'">
        <app-group-management />
      </div>

      <div [style.display]="isActiveParam('verbund-settlements') ? 'block' : 'none'">
        @if (isActiveParam('verbund-settlements')) {
          <app-verbund-settlements />
        }
      </div>

      <div [style.display]="isActiveParam('notifications') ? 'block' : 'none'">
        <app-notification-settings />
      </div>

      <div [style.display]="isActiveParam('payments') ? 'block' : 'none'">
        <!-- A network member has no Stripe account: mounting this would call Stripe anyway. -->
        @if (!company?.is_member) {
          <app-financial-settings />
        }
      </div>

      <div [style.display]="isActiveParam('bank-account') ? 'block' : 'none'">
        <section hlmCard>
          <div hlmCardHeader>
            <h2 hlmCardTitle>Bankverbindung für das Netzwerk</h2>
          </div>
          <div hlmCardContent>
            <app-bank-account-form [audience]="company?.is_member ? 'member' : 'network'" />
          </div>
        </section>
      </div>

      <!-- Rendered only while open: it loads the access overview on creation. Owner-only:
           an agency working in the account gets 403 there. -->
      <div [style.display]="isActiveParam('partner-access') ? 'block' : 'none'">
        @if (isActiveParam('partner-access') && !authService.actingPartnerName()) {
          <app-partner-access-settings />
        }
      </div>

      <div [style.display]="isActiveParam('company') ? 'block' : 'none'">
        <app-company-settings />
      </div>

      <!-- Rendered only while open: they load their data on creation. -->
      <div [style.display]="isActiveParam('accounting-statement') ? 'block' : 'none'">
        @if (isActiveParam('accounting-statement')) {
          <app-accounting-statement />
        }
      </div>

      <div [style.display]="isActiveParam('payout-statements') ? 'block' : 'none'">
        @if (isActiveParam('payout-statements')) {
          <app-payout-statements />
        }
      </div>

      <div [style.display]="isActiveParam('invoice') ? 'block' : 'none'">
        <app-invoicing />
      </div>
    </main>
  </div>
</div>

<!-- Floating voucher widget (hidden for members — they don't sell) -->
@if (company && !company.is_member) {
  <div class="voucher-widget-fixed">
    <schenkwerk-voucher [storeId]="getStoreId()" [asWidget]="true"></schenkwerk-voucher>
  </div>
}
